import { Box, HStack, IconButton, Text, useColorMode } from '@chakra-ui/react';
import { useRef, useCallback } from 'react';
import { MaterialIcon } from './MaterialIcon';
import { formatTime, useAudioPlayer } from '../../hooks/ui/useAudioPlayer';
import { useWaveformCanvas, WAVEFORM_CANVAS_H } from '../../hooks/ui/useWaveformCanvas';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';

export interface AudioPlayerProps {
  src: string;
  /**
   * Which surface is playing the clip, stamped on every playback event.
   *
   * REQUIRED, so a new call site cannot silently omit it. The events used to
   * carry `{}`, which meant a whole surface's playback could vanish from the
   * series — as it did when the capture stream swapped this component for a
   * play tile — with no dimension to attribute the step-down to. A shared
   * union rather than a free string: a typo'd surface is a silent hole in the
   * breakdown, and there are only a handful of players.
   */
  surface: AudioPlayerSurface;
}

/** Every surface that plays a clip. Add the case, then the call site. */
export type AudioPlayerSurface =
  | 'attachment_view'
  | 'attachment_detail'
  | 'quick_capture_review'
  // The desktop recovery dialog, previewing a take still on the device.
  | 'recording_recovery'
  | 'capture_stream';

export function AudioPlayer({ src, surface }: AudioPlayerProps) {
  const { colorMode } = useColorMode();
  const { audioRef, isPlaying, currentTime, duration, progress, togglePlay, seekToRatio } =
    useAudioPlayer(src);
  const { canvasRef, containerRef, playedProbeRef, unplayedProbeRef } = useWaveformCanvas(
    progress,
    colorMode,
  );
  const isDraggingRef = useRef(false);

  const handleTogglePlay = useCallback(() => {
    if (!isPlaying) {
      safeAnalytics.track(ANALYTICS_EVENTS.AUDIO_PLAYER_PLAYED, { surface });
    }
    void togglePlay();
  }, [togglePlay, isPlaying, surface]);

  const seekTo = useCallback(
    (clientX: number) => {
      const el = containerRef.current;
      if (!el || duration <= 0) return;
      const rect = el.getBoundingClientRect();
      seekToRatio((clientX - rect.left) / rect.width);
    },
    [containerRef, duration, seekToRatio],
  );

  // Pointer Events API handles mouse + touch + stylus in one path.
  // setPointerCapture keeps events flowing even when the cursor leaves the element.
  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      isDraggingRef.current = true;
      seekTo(e.clientX);
    },
    [seekTo],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return;
      seekTo(e.clientX);
    },
    [seekTo],
  );

  const onPointerUp = useCallback(() => {
    if (isDraggingRef.current) {
      safeAnalytics.track(ANALYTICS_EVENTS.AUDIO_PLAYER_SEEKED, { method: 'drag', surface });
    }
    isDraggingRef.current = false;
  }, [surface]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (duration <= 0) return;
      const step = 0.05;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        seekToRatio(progress + step);
        safeAnalytics.track(ANALYTICS_EVENTS.AUDIO_PLAYER_SEEKED, { method: 'keyboard', surface });
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        seekToRatio(progress - step);
        safeAnalytics.track(ANALYTICS_EVENTS.AUDIO_PLAYER_SEEKED, { method: 'keyboard', surface });
      } else if (e.key === 'Home') {
        e.preventDefault();
        seekToRatio(0);
        safeAnalytics.track(ANALYTICS_EVENTS.AUDIO_PLAYER_SEEKED, { method: 'keyboard', surface });
      } else if (e.key === 'End') {
        e.preventDefault();
        seekToRatio(1);
        safeAnalytics.track(ANALYTICS_EVENTS.AUDIO_PLAYER_SEEKED, { method: 'keyboard', surface });
      }
    },
    [duration, progress, seekToRatio, surface],
  );

  return (
    <HStack gap={2.5} align="center" w="full">
      {/* Off-screen color probes — Chakra resolves semantic tokens to real RGB for canvas use */}
      <Box
        ref={playedProbeRef}
        aria-hidden="true"
        bg="brand.primary"
        sx={{ position: 'fixed', top: '-9999px', left: '-9999px', width: '1px', height: '1px' }}
      />
      <Box
        ref={unplayedProbeRef}
        aria-hidden="true"
        bg="border.muted"
        sx={{ position: 'fixed', top: '-9999px', left: '-9999px', width: '1px', height: '1px' }}
      />

      <IconButton
        aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
        icon={
          <MaterialIcon
            name={isPlaying ? 'pause' : 'play_arrow'}
            boxSize={5}
            fontSize="1.25rem"
            color="text.onBrand"
          />
        }
        onClick={handleTogglePlay}
        bg="brand.primary"
        _hover={{ bg: 'accent.600' }}
        _active={{ bg: 'accent.700' }}
        borderRadius="full"
        minW="44px"
        h="44px"
        size="sm"
        flexShrink={0}
      />

      {/* Waveform scrubber — canvas for single-pass rendering */}
      <Box
        ref={containerRef}
        flex={1}
        minW={0}
        position="relative"
        h={`${WAVEFORM_CANVAS_H}px`}
        cursor="pointer"
        userSelect="none"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        role="slider"
        aria-label="Audio position"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
        aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
        _focusVisible={{ outline: 'none', boxShadow: 'outline' }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: `${WAVEFORM_CANVAS_H}px`, display: 'block' }}
        />

        {/* Playhead dot */}
        {progress > 0 && progress < 1 && (
          <Box
            position="absolute"
            top="50%"
            left={`${progress * 100}%`}
            transform="translate(-50%, -50%)"
            w="8px"
            h="8px"
            borderRadius="full"
            bg="brand.primary"
            pointerEvents="none"
            sx={{ boxShadow: '0 0 0 2px var(--chakra-colors-bg-surface)' }}
          />
        )}
      </Box>

      <Text
        fontSize="xs"
        color="text.muted"
        flexShrink={0}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatTime(currentTime)}&thinsp;/&thinsp;{formatTime(duration)}
      </Text>

      {/* Native audio element — parked off-screen (not display:none) so browsers honour preload="metadata" */}
      <Box
        as="audio"
        ref={audioRef}
        src={src}
        preload="metadata"
        aria-hidden="true"
        sx={{ position: 'fixed', top: '-9999px', left: '-9999px', width: '1px', height: '1px' }}
      />
    </HStack>
  );
}
